<template>
  <div class="rd-screen">
    <report-tools :copy="rows.length > 0" @copy="copyTable"></report-tools>

    <report-state v-if="loading && !rows.length" kind="loading"></report-state>
    <report-state v-else-if="!sortedRows.length" icon="mdi-silverware-fork-knife" :title="emptyTitle" :text="emptyText"></report-state>

    <ui-table
      v-else
      variant="report"
      :columns="columns"
      :rows="sortedRows"
      :row-key="(row) => row.key"
      :sort="sort"
      :totals="footTotals"
      :total-label="$t('total')"
      @sort="sortBy"
    >
      <template #cell-num="{ index }">{{ index + 1 }}</template>

      <template #cell-quantity="{ row }">
        <b>{{ row.quantity }}</b>
      </template>

      <template #cell-share="{ row }">
        <span class="rd-share">
          <span class="rd-mini"><i :style="{ width: `${row.share}%` }"></i></span>
          <span class="rd-num">{{ row.share }}%</span>
        </span>
      </template>
    </ui-table>
  </div>
</template>

<script>
import hasTableSort from "@/components/report/desktop/hasTableSort";
import ReportState from "@/components/report/desktop/ReportState.vue";
import ReportTools from "@/components/report/desktop/ReportTools.vue";
import { copyText, rowsToTsv } from "@/components/report/desktop/copyTable";

/**
 * Obroci i Jela gostiju: količina po jelu sa udelom u ukupnom. Ne učitava ništa; podatke daje
 * ekran koji ga koristi (stari tab sa nizom { id, name, quantity, prilog }).
 */
export default {
  name: "GuestMealsView",
  mixins: [hasTableSort],
  components: { ReportState, ReportTools },
  props: {
    rows: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    search: { type: String, default: "" },
    /** Kolona sa napomenama (prilozi), samo u "Jela" */
    showNotes: { type: Boolean, default: false },
  },
  data: () => ({
    sort: { key: "quantity", dir: -1 },
  }),
  computed: {
    /** Kolone tabele: iste služe za zaglavlje, redove i zbirni red. */
    columns() {
      return [
        { key: "num", label: "#", headCls: "rd-col-num", num: true, zero: () => true },
        { key: "name", label: this.$t("food"), name: true, sortable: true },
        ...(this.showNotes ? [{ key: "prilog", label: this.$t("notes"), muted: true }] : []),
        { key: "quantity", label: this.$t("quantity"), align: "end", num: true, sortable: true },
        { key: "share", label: this.$t("report_desktop.share"), headCls: "rd-col-share" },
      ];
    },
    footTotals() {
      return { quantity: this.total };
    },
    total() {
      return this.matched.reduce((sum, row) => sum + (Number(row.quantity) || 0), 0);
    },
    matched() {
      const rows = this.rows.map((row) => ({
        key: String(row.id ?? row.name),
        name: this.toLatin(row.name || ""),
        quantity: Number(row.quantity) || 0,
        prilog: this.toLatin(row.prilog || ""),
      }));
      return this.customSearch(rows, [{ value: "name" }, { value: "prilog" }], this.search);
    },
    sortedRows() {
      return this.sortRows(
        this.matched.map((row) => ({
          ...row,
          share: this.total ? Math.round((row.quantity / this.total) * 100) : 0,
        }))
      );
    },
    emptyTitle() {
      return this.search
        ? this.$t("report_mobile.no_results_for", { search: this.search })
        : this.$t("no_data_available");
    },
    emptyText() {
      return this.search ? "" : this.$t("report_desktop.empty_hint");
    },
    kpis() {
      return [
        { key: "total", label: this.$t("report_desktop.total_meals"), value: this.total, tone: "inverse" },
        { key: "meals", label: this.$t("meals"), value: this.matched.length },
      ];
    },
  },
  watch: {
    kpis: {
      handler(kpis) {
        this.$emit("kpis", this.rows.length ? kpis : []);
      },
      immediate: true,
    },
  },
  methods: {
    async copyTable() {
      const headers = [this.$t("food"), ...(this.showNotes ? [this.$t("notes")] : []), this.$t("quantity")];
      const rows = this.sortedRows.map((row) => [row.name, ...(this.showNotes ? [row.prilog] : []), row.quantity]);
      const copied = await copyText(rowsToTsv(headers, rows));
      this.$emit("toast", this.$t(copied ? "report_desktop.copied" : "report_desktop.copy_failed"));
    },
  },
};
</script>
